import { ensureIdentifiers } from './session';

export interface SendTextRequest {
  message: string;
  signal?: AbortSignal;
}

export interface ChatResponse {
  response: string;
  hideActionButtons?: boolean;
  /** `false` = sem conteúdo aproveitável; esconde ações da resposta. US 13334. */
  has_content?: boolean;
}

export interface PodcastRequest {
  topic: string;
  context: string;
  durationMinutes?: number;
  signal?: AbortSignal;
}

export interface PodcastResponse {
  audio_url: string;
  gs_url: string;
  duration_seconds: number;
}

export interface ChatApi {
  sendText(req: SendTextRequest): Promise<ChatResponse>;
  sendAudio(blob: Blob, mimeType: string, signal?: AbortSignal): Promise<ChatResponse>;
  generatePodcast(req: PodcastRequest, timeoutMs: number): Promise<PodcastResponse>;
  downloadPodcast(audioUrl: string, signal?: AbortSignal): Promise<void>;
}

export function createChatApi(baseUrl: string): ChatApi {
  const url = (path: string) => `${baseUrl}${path}`;

  return {
    async sendText({ message, signal }) {
      const { userId, sessionId } = ensureIdentifiers();
      const res = await fetch(url('/v1/chat'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ user_id: userId, session_id: sessionId, message }),
        ...(signal && { signal }),
      });
      if (!res.ok) throw new ChatHttpError(res.status, await safeText(res));
      return (await res.json()) as ChatResponse;
    },

    async sendAudio(blob, mimeType, signal) {
      const { userId, sessionId } = ensureIdentifiers();
      const ext = mimeTypeToExt(mimeType);
      const form = new FormData();
      form.append('file', blob, `recording.${ext}`);
      form.append('user_id', userId);
      form.append('session_id', sessionId);
      const res = await fetch(url('/v1/chat/audio'), {
        method: 'POST',
        body: form,
        ...(signal && { signal }),
      });
      if (!res.ok) throw new ChatHttpError(res.status, await safeText(res));
      return (await res.json()) as ChatResponse;
    },

    async generatePodcast({ topic, context, durationMinutes = 2, signal }, timeoutMs) {
      const ctrl = new AbortController();
      const onAbort = () => ctrl.abort(signal?.reason);
      signal?.addEventListener('abort', onAbort);
      const timer = setTimeout(
        () => ctrl.abort(new DOMException('timeout', 'TimeoutError')),
        timeoutMs,
      );
      try {
        const res = await fetch(url('/v1/chat/podcast'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            topic: topic.slice(0, 1500),
            context: context.slice(0, 15_000),
            duration_minutes: durationMinutes,
          }),
          signal: ctrl.signal,
        });
        if (!res.ok) throw new ChatHttpError(res.status, await safeText(res));
        return (await res.json()) as PodcastResponse;
      } finally {
        clearTimeout(timer);
        signal?.removeEventListener('abort', onAbort);
      }
    },

    async downloadPodcast(audioUrl, signal) {
      const res = await fetch(url('/v1/podcast/download'), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ fileUrl: audioUrl }),
        ...(signal && { signal }),
      });
      if (!res.ok) throw new ChatHttpError(res.status, await safeText(res));
      const blob = await res.blob();
      const objectUrl = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = objectUrl;
      a.download = filenameFromUrl(audioUrl);
      document.body.appendChild(a);
      a.click();
      a.remove();
      // Defer revoke by a microtask: Chromium/Safari race a synchronous
      // revoke against the browser's read of the URL and produce empty files.
      setTimeout(() => {
        URL.revokeObjectURL(objectUrl);
      }, 0);
    },
  };
}

export class ChatHttpError extends Error {
  readonly status: number;
  readonly body: string;
  constructor(status: number, body: string) {
    super(`HTTP ${status}: ${body || '(empty body)'}`);
    this.name = 'ChatHttpError';
    this.status = status;
    this.body = body;
  }
}

function mimeTypeToExt(mime: string): string {
  if (mime.includes('webm')) return 'webm';
  if (mime.includes('mp4')) return 'mp4';
  if (mime.includes('ogg')) return 'ogg';
  if (mime.includes('wav')) return 'wav';
  return 'bin';
}

function filenameFromUrl(u: string): string {
  try {
    const path = new URL(u).pathname;
    const last = path.split('/').findLast(Boolean);
    return last ?? 'podcast.mp3';
  } catch {
    return 'podcast.mp3';
  }
}

async function safeText(res: Response): Promise<string> {
  try {
    return await res.text();
  } catch {
    return '';
  }
}
